import { VerifiedOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Menu, MenuItem, Select, Snackbar } from "@mui/material";
import Box from "@mui/material/Box";
import { useTheme } from "components/Reusable/ThemeProvider";
import {
  Bell,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
} from "lucide-react";
import { default as React, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { connect } from "react-redux";
import { Navigate, useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import { bindActionCreators } from "redux";
import i18n from "../../../config/Language/i18n";
import { Notification } from "../../../models/notification";
import { UserConversation } from "../../../models/userConversation";
import { AxiosHttpClient } from "../../../services/AxiosHttpService";
import { Dispatch, RootState } from "../../../store";
import { logOutAction } from "../../../store/Signin/actions";
import { isUserSigninSucceed } from "../../../store/selectors";
import { FlexContainer } from "../../../styles/FlexContainer";
import { Text14, Text16 } from "../../../styles/Text";
import { CustomTheme } from "../../../styles/Theme";
import { useAuth } from "../../Reusable/AuthProvider";
import AvatarWithInitials from "../../Reusable/AvatarWithInitials";
import { useSettings } from "../../Reusable/SettingsProvider";
import { useChat } from "../../Reusable/UseUserChat";
import {
  getChatOtherUser,
  getChatOtherUserFullName,
  getRelativeTime,
} from "../../Reusable/Utils";
import { useWebSocket } from "../../Reusable/useWebSocket";
import ChatContainer from "../ContentSection/UserChat/ChatContainer";

export interface FeatureHeaderProps {
  sideNavShow: boolean;
  isUserSigninSucceed: boolean;
  logOut: typeof logOutAction;
  setSideNavShow: React.Dispatch<React.SetStateAction<boolean>>;
}

const _FeatureHeader: React.FC<FeatureHeaderProps> = ({
  sideNavShow,
  isUserSigninSucceed,
  logOut,
  setSideNavShow,
}) => {
  const { settings, updateSettings } = useSettings();

  const getNotificationsPage = (pageNumber?: number) => {
    AxiosHttpClient.get<any>("api/v1/docuveritas/notifications/page", {
      criteriaList: "",
      pageNumber: (pageNumber ? pageNumber : page) - 1,
      pageSize: 20,
      sort: "creationDateTime",
      order: "desc",
    })
      .then((res) => {
        setTotalNumberOfNotifications(Number(res.totalElements));
        (pageNumber ? pageNumber : page) === 1
          ? setNotifications([...res.content])
          : setNotifications([...notifications, ...res.content]);
      })
      .catch((err) => {});
  };

  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
    const settingsLang = settings?.language; // Depuis la BD
    const cachedLang = localStorage.getItem("language");
    return settingsLang || cachedLang || i18n.language || "en"; // Priorité BD > localStorage > i18n > default
  });

  // Effet pour synchroniser avec les settings de la BD
  useEffect(() => {
    if (settings?.language && settings.language !== selectedLanguage) {
      setSelectedLanguage(settings.language);
    }
  }, [settings?.language]);

  useEffect(() => {
    i18n.changeLanguage(selectedLanguage); // Apply the selected language
    localStorage.setItem("language", selectedLanguage); // Cache the selected language
    updateSettings({
      language: selectedLanguage,
    });
  }, [selectedLanguage]);

  const markAllAsRead = () => {
    AxiosHttpClient.put("api/v1/docuveritas/notifications/read").then((res) => {
      getNotificationsPage(1);
      setPage(1);
      setHasMore(true);
    });
  };

  let fetchMoreData: any = () => {
    if (page > totalNumberOfNotifications / 20) {
      setHasMore(false);
      return;
    }
    setPage(page + 1);
  };

  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const [notifications, setNotifications] = useState<Notification[]>([]);

  const [page, setPage] = useState(1);

  const [totalNumberOfNotifications, setTotalNumberOfNotifications] =
    useState(0);

  const [hasMore, setHasMore] = useState(true);

  const [notification, setNotification] = useState<Notification | null>(null);

  const [anchorElNotifications, setAnchorElNotifications] =
    React.useState<null | HTMLElement>(null);

  const [open, setOpen] = useState(false);

  const audioPlayer = useRef<any>(null);

  const { subscribe, publish } = useWebSocket();

  const { openChats, minimizedChats, messageCache } = useChat();

  const { t } = useTranslation();

  const { openChat } = useChat();

  const { user } = useAuth();

  const { resolvedTheme } = useTheme();

  const getNotificationIcon = (notification: Notification | null) => {
    if (notification) {
      switch (notification.actionType) {
        case "CERTIFICATION_REQUEST":
          return <VerifiedOutlined color="primary" />;
        case "CERTIFICATION_RESPONSE":
          return <VerifiedOutlined color="primary" />;

        default:
          return <VerifiedOutlined color="primary" />;
      }
    }
  };

  useEffect(() => {
    if (notification) {
      setOpen(true);
      setTimeout(() => {
        setOpen(false);
      }, 10000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  useEffect(() => {
    if (page !== 1) getNotificationsPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    getNotificationsPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/signin" && !localStorage.getItem("jwtToken"))
      window.location.href = `${window.location.origin}/signin`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (!user) return;

    // ces subscribe sont appelés via pendingSubscriptions si pas encore connecté
    const subNotif = subscribe(`/topic/${user.id}/notifications`, (msg) => {
      const n = JSON.parse(msg.body) as Notification;
      setNotification(n);
      //audioPlayer.current!.play();
      getNotificationsPage(1);
    });

    // heartbeat
    const hb = setInterval(() => {
      publish("/app/heartbeat", { userId: user.id });
    }, 30000);

    return () => {
      subNotif.unsubscribe();
      clearInterval(hb);
    };
  }, [user, subscribe, publish]);

  const navigate = useNavigate();

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [userConversations, setUserConversations] = useState<
    UserConversation[]
  >([]); // Replace `any` with the appropriate type
  const [anchorElChat, setAnchorElChat] = useState<null | HTMLElement>(null);

  const getChatConversations = () => {
    AxiosHttpClient.get<any>("api/v1/docuveritas/user-conversations").then(
      (res) => {
        setUserConversations(res);
      }
    );
  };

  useEffect(() => {
    if (isChatOpen) {
      getChatConversations();
    }
  }, [isChatOpen]);

  // Ajout : Rafraîchir les conversations à chaque changement du cache des messages
  useEffect(() => {
    getChatConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageCache]);

  return (
    <>
      {!isUserSigninSucceed && <Navigate to="/signin" replace />}

      <Box
        component="header"
        sx={{
          backgroundColor: resolvedTheme === "dark" ? "#1f2937" : "white",
          borderBottom:
            resolvedTheme === "dark"
              ? "1px solid #374151"
              : "1px solid #e0e0e0",
          padding: "16px",
          width: "100%",
          overflowX: "none",
        }}
      >
        <FlexContainer
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <FlexContainer sx={{ alignItems: "center", gap: "16px" }}>
            <IconButton
              onClick={() => setSideNavShow(!sideNavShow)}
              sx={{
                padding: "8px",
                ":hover": {
                  backgroundColor:
                    resolvedTheme === "dark" ? "#374151" : "#F3F4F6",
                },
                borderRadius: "8px",
                transition: "all 0.3s ease",
                color: resolvedTheme === "dark" ? "white" : "black",
                border: "none",
                background: "none",
                cursor: "pointer",
                outline: "none",
              }}
            >
              {sideNavShow ? (
                <PanelLeftClose size={24} />
              ) : (
                <PanelLeftOpen size={24} />
              )}
            </IconButton>
            <Box
              component="h1"
              sx={{
                fontSize: "1.25rem",
                fontWeight: "600",
                lineHeight: "24px",
                margin: 0,
                color: resolvedTheme === "dark" ? "white" : "black",
                fontFamily:
                  'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {t("HEADER_TITLE")}
            </Box>
          </FlexContainer>

          <FlexContainer sx={{ alignItems: "center", gap: "8px" }}>
            {/* Search Bar */}
            <Box
              sx={{
                position: "relative",
                display: { xs: "none", md: "block" },
              }}
            >
              <Box
                component="input"
                type="text"
                placeholder={t("HEADER_SEARCH_PLACEHOLDER")}
                sx={{
                  width: "256px",
                  padding: "8px 16px",
                  paddingLeft: "40px",
                  backgroundColor:
                    resolvedTheme === "dark" ? "#374151" : "#F3F4F6",
                  border:
                    resolvedTheme === "dark"
                      ? "1px solid #4b5563"
                      : "1px solid #e0e0e0",
                  borderRadius: "8px",
                  lineHeight: "24px",
                  fontSize: "16px",
                  fontFamily: "inherit",
                  color: resolvedTheme === "dark" ? "white" : "black",
                  "::placeholder": {
                    color:
                      resolvedTheme === "dark"
                        ? "rgba(255, 255, 255, 0.4)"
                        : "rgba(0, 0, 0, 0.4)",
                    opacity: 1,
                  },
                  ":focus": {
                    outline: "none",
                    boxShadow: "0 0 0 2px #1976d2",
                    borderColor: "transparent",
                  },
                }}
              />
              <Search
                size={20}
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "10px",
                  color: resolvedTheme === "dark" ? "#9ca3af" : "#9e9e9e",
                }}
              />
            </Box>

            {/* Notifications */}
            <Box sx={{ position: "relative" }}>
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setAnchorElNotifications(e.currentTarget);
                }}
                sx={{
                  color: "hsl(var(--foreground))",
                  padding: "8px",
                  ":hover": {
                    backgroundColor: "hsl(var(--muted))",
                  },
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                  position: "relative",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                <Bell size={24} />
                {notifications.filter((notification) => !notification.read)
                  .length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "4px",
                      right: "4px",
                      backgroundColor: "hsl(var(--destructive))",
                      color: "hsl(var(--destructive-foreground))",
                      fontSize: "0.75rem",
                      borderRadius: "50%",
                      width: "16px",
                      height: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {
                      notifications.filter((notification) => !notification.read)
                        .length
                    }
                  </Box>
                )}
              </IconButton>

              <Menu
                id="invoice-menu"
                anchorEl={anchorElNotifications}
                open={Boolean(anchorElNotifications)}
                onClose={(e) => {
                  markAllAsRead();
                  setAnchorElNotifications(null);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  id: "notificationsPaperId",
                  elevation: 0,
                  sx: {
                    overflowY: "auto",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                    mt: 1.5,
                    marginX: "20px",
                    maxWidth: "95vw",
                    maxHeight: "600px",
                    width: "450px",
                    padding: "8px",
                    bgcolor: "hsl(var(--card))",
                    color: "hsl(var(--foreground))",
                    border: "1px solid hsl(var(--border))",
                  },
                }}
              >
                <InfiniteScroll
                  dataLength={notifications.length}
                  next={fetchMoreData}
                  hasMore={hasMore}
                  loader={
                    <FlexContainer justifyContent="center" margin="0 0 16px">
                      <Text14 fontWeight="600">Chargement...</Text14>
                    </FlexContainer>
                  }
                  scrollableTarget="notificationsPaperId"
                >
                  <FlexContainer>
                    <Text16
                      fontWeight="600"
                      margin="12px 16px"
                      color="hsl(var(--foreground))"
                    >
                      Notifications
                    </Text16>
                  </FlexContainer>
                  {notifications.length === 0 && (
                    <FlexContainer
                      flexDirection={"column"}
                      alignItems={"flex-start"}
                      flex={"1"}
                      overflow={"hidden"}
                    >
                      <Text14
                        lineHeight="20px"
                        textWrap="wrap"
                        margin="0 0 16px 16px"
                        color="hsl(var(--muted-foreground))"
                      >
                        {"Vous n'avez aucune notification"}
                      </Text14>
                    </FlexContainer>
                  )}
                  {notifications.map((notification) => (
                    <MenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAnchorElNotifications(null);
                        navigate(notification!.url);
                      }}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: notification.read
                          ? "hsl(var(--card))"
                          : "hsl(var(--muted))",
                        color: "hsl(var(--foreground))",
                        "&:hover": {
                          backgroundColor: "hsl(var(--accent))",
                          color: "hsl(var(--accent-foreground))",
                        },
                      }}
                    >
                      <FlexContainer marginTop={"4px"}>
                        <FlexContainer
                          width={"40px"}
                          height={"40px"}
                          justifyContent={"center"}
                          alignItems={"center"}
                          margin={"4px 0 8px 0"}
                          sx={{
                            backgroundColor: "hsl(var(--muted))",
                            borderRadius: "8px",
                          }}
                        >
                          {getNotificationIcon(notification)}
                        </FlexContainer>
                        <FlexContainer
                          flexDirection={"column"}
                          alignItems={"flex-start"}
                          flex={"1"}
                          overflow={"hidden"}
                        >
                          <Text14
                            lineHeight="20px"
                            textWrap="wrap"
                            fontWeight={notification.read ? "400" : "500"}
                            color="hsl(var(--foreground))"
                          >
                            {notification?.message}
                          </Text14>
                          <Text14
                            lineHeight="20px"
                            fontWeight={notification.read ? "400" : "500"}
                            color="hsl(var(--muted-foreground))"
                          >
                            {getRelativeTime(notification?.creationDateTime, t)}
                          </Text14>
                        </FlexContainer>
                      </FlexContainer>
                    </MenuItem>
                  ))}
                </InfiniteScroll>
              </Menu>
            </Box>

            {/* Chats */}
            <Box sx={{ position: "relative" }}>
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setAnchorElChat(e.currentTarget);
                  setIsChatOpen(!isChatOpen);
                }}
                sx={{
                  color: "hsl(var(--foreground))",
                  padding: "8px",
                  ":hover": {
                    backgroundColor: "hsl(var(--muted))",
                  },
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                  position: "relative",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                <MessageSquare size={24} />
                {/* Affichage du badge pour les conversations non lues */}
                {(() => {
                  // On compte le nombre de conversations où le dernier message est non lu et envoyé par l'autre utilisateur
                  let unreadConversations = 0;
                  if (
                    user &&
                    userConversations &&
                    userConversations.length > 0
                  ) {
                    unreadConversations = userConversations.filter((conv) => {
                      // Récupérer le cache des messages pour cette conversation
                      const cache =
                        messageCache?.[conv.usersKeyOrCertificationId];
                      if (
                        !cache ||
                        !cache.messages ||
                        cache.messages.length === 0
                      )
                        return false;
                      const lastMsg = cache.messages[cache.messages.length - 1];
                      // Non lu, envoyé par l'autre utilisateur
                      return lastMsg.unread && lastMsg.sender.id !== user.id;
                    }).length;
                  }
                  return unreadConversations > 0 ? (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "4px",
                        right: "4px",
                        backgroundColor: "hsl(var(--destructive))",
                        color: "hsl(var(--destructive-foreground))",
                        fontSize: "0.75rem",
                        borderRadius: "50%",
                        width: "16px",
                        height: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                      }}
                    >
                      {unreadConversations > 9 ? "9+" : unreadConversations}
                    </Box>
                  ) : null;
                })()}
              </IconButton>

              <Menu
                id="chat-menu"
                anchorEl={anchorElChat}
                open={Boolean(anchorElChat)}
                onClose={(e) => {
                  setAnchorElChat(null);
                  setIsChatOpen(false);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  id: "chatPaperId",
                  elevation: 0,
                  sx: {
                    overflowY: "auto",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                    mt: 1.5,
                    marginX: "20px",
                    maxWidth: "95vw",
                    maxHeight: "600px",
                    width: "450px",
                    padding: "8px",
                    bgcolor: "hsl(var(--card))",
                    color: "hsl(var(--foreground))",
                    border: "1px solid hsl(var(--border))",
                  },
                }}
              >
                <FlexContainer>
                  <Text16
                    fontWeight="600"
                    margin="12px 16px"
                    color="hsl(var(--foreground))"
                  >
                    Conversations
                  </Text16>
                </FlexContainer>
                {userConversations.length === 0 && (
                  <FlexContainer
                    flexDirection={"column"}
                    alignItems={"flex-start"}
                    flex={"1"}
                    overflow={"hidden"}
                  >
                    <Text14
                      lineHeight="20px"
                      textWrap="wrap"
                      margin="0 0 16px 16px"
                      color="hsl(var(--muted-foreground))"
                    >
                      {"Vous n'avez aucune conversation"}
                    </Text14>
                  </FlexContainer>
                )}
                {userConversations.map((conversation) => (
                  <MenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAnchorElChat(null);
                      openChat(conversation.usersKeyOrCertificationId);
                    }}
                    sx={{
                      borderRadius: "8px",
                      backgroundColor: "hsl(var(--card))",
                      color: "hsl(var(--foreground))",
                      "&:hover": {
                        backgroundColor: "hsl(var(--accent))",
                        color: "hsl(var(--accent-foreground))",
                      },
                    }}
                  >
                    <FlexContainer marginTop={"4px"}>
                      <AvatarWithInitials
                        user={getChatOtherUser(conversation, user?.id)}
                        sx={{
                          width: "40px",
                          height: "40px",
                          marginRight: "8px",
                        }}
                      />
                      <FlexContainer
                        flexDirection={"column"}
                        alignItems={"flex-start"}
                        flex={"1"}
                        overflow={"hidden"}
                      >
                        <Text14
                          lineHeight="20px"
                          fontWeight="500"
                          color="hsl(var(--foreground))"
                        >
                          {getChatOtherUserFullName(conversation, user?.id)}
                        </Text14>
                        <Text14
                          lineHeight="20px"
                          color="hsl(var(--muted-foreground))"
                        >
                          {conversation.lastMessageText}
                        </Text14>
                      </FlexContainer>
                    </FlexContainer>
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                <Select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  displayEmpty
                  inputProps={{ "aria-label": "Language selection" }}
                  sx={{
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px",
                      border: "none", // Remove border
                      background: "none", // Remove background
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none", // Remove border outline
                    },
                  }}
                  renderValue={(value) => {
                    const flagMap: Record<string, string> = {
                      en: "https://flagcdn.com/w40/us.png",
                      fr: "https://flagcdn.com/w40/fr.png",
                      ar: "https://flagcdn.com/w40/sa.png",
                    };
                    return (
                      <img
                        src={flagMap[value as string]}
                        alt={value}
                        style={{
                          width: "24px",
                          height: "16px",
                          borderRadius: "4px",
                        }}
                      />
                    );
                  }}
                >
                  <MenuItem value="en">
                    <img
                      src="https://flagcdn.com/w40/us.png"
                      alt="English"
                      style={{
                        width: "24px",
                        height: "16px",
                        borderRadius: "4px",
                        marginRight: "8px",
                      }}
                    />
                    English
                  </MenuItem>
                  <MenuItem value="fr">
                    <img
                      src="https://flagcdn.com/w40/fr.png"
                      alt="French"
                      style={{
                        width: "24px",
                        height: "16px",
                        borderRadius: "4px",
                        marginRight: "8px",
                      }}
                    />
                    French
                  </MenuItem>
                  <MenuItem value="ar">
                    <img
                      src="https://flagcdn.com/w40/sa.png"
                      alt="Arabic"
                      style={{
                        width: "24px",
                        height: "16px",
                        borderRadius: "4px",
                        marginRight: "8px",
                      }}
                    />
                    Arabic
                  </MenuItem>
                </Select>
              </Box>
            </Box>

            {/* Profile */}
            <Box sx={{ position: "relative" }}>
              <Box
                component="button"
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px",
                  ":hover": {
                    backgroundColor:
                      resolvedTheme === "dark" ? "#374151" : "#F3F4F6",
                  },
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                <AvatarWithInitials
                  user={user}
                  sx={{
                    width: "32px",
                    height: "32px",
                    border: "2px solid",
                    borderColor: "grey.200",
                  }}
                />
              </Box>

              {isProfileOpen && (
                <Box
                  sx={{
                    position: "absolute",
                    right: 0,
                    marginTop: "8px",
                    width: "192px",
                    backgroundColor:
                      resolvedTheme === "dark" ? "#374151" : "white",
                    borderRadius: "8px",
                    boxShadow:
                      "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)",
                    border:
                      resolvedTheme === "dark"
                        ? "1px solid #4b5563"
                        : "1px solid #e0e0e0",
                    padding: "8px 0",
                    zIndex: 50,
                  }}
                >
                  <Box
                    component="a"
                    href={"/feature/profile/" + user?.id}
                    sx={{
                      display: "block",
                      padding: "8px 16px",
                      fontSize: "0.875rem",
                      color: resolvedTheme === "dark" ? "#e5e7eb" : "#424242",
                      ":hover": {
                        backgroundColor:
                          resolvedTheme === "dark" ? "#4b5563" : "#F3F4F6",
                      },
                    }}
                  >
                    Mon Profil
                  </Box>
                  <Box
                    component="a"
                    href="/feature/settings"
                    sx={{
                      display: "block",
                      padding: "8px 16px",
                      fontSize: "0.875rem",
                      color: resolvedTheme === "dark" ? "#e5e7eb" : "#424242",
                      ":hover": {
                        backgroundColor:
                          resolvedTheme === "dark" ? "#4b5563" : "#F3F4F6",
                      },
                    }}
                  >
                    Paramètres
                  </Box>
                  <Box
                    component="hr"
                    sx={{
                      margin: "8px 0",
                      borderColor:
                        resolvedTheme === "dark" ? "#4b5563" : "#e0e0e0",
                    }}
                  />
                  <Box
                    component="a"
                    href="#"
                    sx={{
                      display: "block",
                      padding: "8px 16px",
                      fontSize: "0.875rem",
                      color: "#d32f2f",
                      ":hover": {
                        backgroundColor:
                          resolvedTheme === "dark" ? "#4b5563" : "#F3F4F6",
                      },
                    }}
                    onClick={() => {
                      localStorage.clear();
                      logOut();
                    }}
                  >
                    Déconnexion
                  </Box>
                </Box>
              )}
            </Box>
          </FlexContainer>
        </FlexContainer>

        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          open={open}
        >
          <FlexContainer
            id="client-snackbar"
            elevation={3}
            backgroundColor={resolvedTheme === "dark" ? "#374151" : "white"}
            borderRadius={"8px"}
            flexDirection={"column"}
            padding={"16px"}
            maxWidth={"450px"}
            $cursorHover="pointer"
            onClick={(e) => {
              navigate(notification!.url);
            }}
          >
            <FlexContainer justifyContent={"space-between"}>
              <Text14 lineHeight="20px" fontWeight="500">
                Nouvelle notification
              </Text14>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(false);
                }}
              >
                <CloseIcon fontSize="small" color="secondary" />
              </IconButton>
            </FlexContainer>
            <FlexContainer marginTop={"4px"}>
              <FlexContainer
                width={"40px"}
                height={"40px"}
                backgroundColor={CustomTheme.palette.background.default}
                justifyContent={"center"}
                alignItems={"center"}
                margin={"4px 0 8px 0"}
              >
                {getNotificationIcon(notification)}
              </FlexContainer>
              <FlexContainer flexDirection={"column"} alignItems={"flex-start"}>
                <Text14 lineHeight="20px">{notification?.message}</Text14>
                <Text14
                  lineHeight="20px"
                  color={CustomTheme.palette.secondary.main}
                >
                  {getRelativeTime(notification?.creationDateTime, t)}
                </Text14>
              </FlexContainer>
            </FlexContainer>
          </FlexContainer>
        </Snackbar>
        <div id="chat-container">
          <ChatContainer />
        </div>
        <audio
          ref={audioPlayer}
          src={
            "https://weevent.s3.eu-west-3.amazonaws.com/notification-sound.mp3"
          }
        />
      </Box>
    </>
  );
};

export const FeatureHeader = connect(
  (state: RootState) => ({
    isUserSigninSucceed: isUserSigninSucceed(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        logOut: logOutAction,
      },
      dispatch
    )
)(_FeatureHeader);

export default _FeatureHeader;
